import { matchRoutes, useLocation } from '@/lib/router-dom/index';
import classname from 'classnames';
import { useEffect, useState } from 'react';

const initializeRouteStates = (routes, matches) => {
	return routes.reduce((acc, { path }) => {
		const isMatch = matches.some((match) => match.route.path === path);
		acc[path] = {
			isMatch,
			isVisible: isMatch,
		};
		return acc;
	}, {});
};

const computeUpdatedRouteStates = (routes, prevStates, newMatches) => {
	let hasChanged = false;

	const updatedStates = routes.reduce((acc, { path }) => {
		const isMatch = newMatches.some((match) => match.route.path === path);
		const isVisible =
			isMatch ? true : prevStates[path].isVisible && prevStates[path].isMatch === isMatch;

		if (prevStates[path].isMatch !== isMatch || prevStates[path].isVisible !== isVisible) {
			hasChanged = true;
		}

		acc[path] = {
			...prevStates[path],
			isMatch,
			isVisible,
		};
		return acc;
	}, {});

	return { updatedStates, hasChanged };
};

const computeRouteClasses = (routeStates) => {
	return Object.entries(routeStates).reduce((acc, [path, { isMatch }]) => {
		acc[path] = classname('navigation-item', {
			'navigation-item--active': isMatch,
		});
		return acc;
	}, {});
};

const useRouteMatchState = (routes) => {
	const { currentPath } = useLocation();
	const matches = matchRoutes(routes, currentPath) || [];

	const [routeStates, setRouteStates] = useState(() =>
		initializeRouteStates(routes, matches)
	);

	useEffect(() => {
		const newMatches = matchRoutes(routes, currentPath) || [];

		setRouteStates((prevStates) => {
			const { updatedStates, hasChanged } = computeUpdatedRouteStates(
				routes,
				prevStates,
				newMatches
			);
			return hasChanged ? updatedStates : prevStates;
		});
	}, [currentPath, routes]);

	const toggleVisibility = (path) => {
		setRouteStates((prevStates) => ({
			...prevStates,
			[path]: { ...prevStates[path], isVisible: !prevStates[path].isVisible },
		}));
	};

	const routeClasses = computeRouteClasses(routeStates);

	return { routeStates, toggleVisibility, routeClasses };
};

export default useRouteMatchState;
